import React from 'react';
import styled, { keyframes } from 'styled-components';
import lockr from 'utils/local-store';
/* eslint-disable import/no-extraneous-dependencies */
import AcceptIcon from 'icons/accept-bordered';
import CancelIcon from 'icons/cancel-bordered';
import EditIcon from 'icons/pencil-bordered';
import LockIcon from 'icons/lock';
import { EDITABLE_INPUT_FIELD_ACTIVE } from 'constants/ui';
import StatusWarningBorderedIcon from 'icons/status-warning-bordered';
import { EditRevertContext } from './revert-context';
import FjInput from '../fj-input';
import { FjTooltip } from '../fj-tooltip';
import { FjPortal } from '../fj-portal';

const AlertIcon = styled(StatusWarningBorderedIcon).attrs(({ theme }) => ({
  style: {
    primaryColor: theme.colors.accentText,
  },
  size: '1.1em',
}))`
  display: inline;
  margin-right: 0.4em;
`;

interface FloatingErrorBaseProps {
  children?: React.ReactNode;
  // Forwarded by styled-components `.attrs`/generated className; unused at runtime
  style?: React.CSSProperties;
  className?: string;
}

const FloatingErrorBase = ({ children }: FloatingErrorBaseProps) => (
  <FjTooltip Component={children}>
    <AlertIcon />
    {children}
  </FjTooltip>
);

const FloatingError = styled(FloatingErrorBase).attrs(({ theme }) => ({
  style: {
    borderColor: theme.colors.accentText,
    backgroundColor: 'white',
  },
}))<{ position: DOMRect }>`
  position: absolute;
  top: ${({ position }) => position.top + position.height + 20}px;
  left: ${({ position }) => position.left}px;
  color: ${({ theme }) => theme.colors.accentText}px;
`;

const inputAnimation = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const acceptIconAnimation = keyframes`
  from {
    transform: translateX(calc(100% + 5px));
  }
  to {
    transform: translateX(0);
  }
`;

const cancelIconAnimation = keyframes`
  from {
    transform: opacity(0);
  }
  to {
    transform: opacity(1);
  }
`;

const Container = styled.div<{ readOnly?: boolean; value?: string; editing?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ readOnly, value }) => (readOnly && !value ? 'center' : 'space-between')};
  flex: 1;
  margin-bottom: ${(props) => (props.editing ? '-3px' : '')};
`;

const Input = styled(FjInput).attrs({
  height: 'auto',
})<{ textAlign?: string }>`
  margin-right: 10px;
  flex: 1;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -2px;
    height: 1px;
    background-color: currentColor;
    transform-origin: left;
    animation: 200ms ${inputAnimation} ease;
    animation-fill-mode: forwards;
  }

  input {
    flex: 1;
    border-width: 0;
    font-size: inherit;
    padding: 0px 0 3px;
    border-color: inherit;
    background-color: transparent;
    text-align: ${({ textAlign }) => textAlign};
  }
`;

const IconContainer = styled.div<{
  readOnly?: boolean;
  $animation?: ReturnType<typeof keyframes>;
  displayOnHover?: boolean;
  disabled?: boolean;
  primaryColor?: string;
}>`
  display: flex;
  align-items: center;
  margin: 0 0 0 5px;
  cursor: ${({ readOnly }) => (readOnly ? 'default' : 'pointer')};
  animation: 200ms ${(props) => props.$animation} ease;
  animation-fill-mode: forwards;
  opacity: ${({ displayOnHover, disabled }) =>
    (displayOnHover && '0') || (disabled && '0.4') || '1'};
  transition: opacity 100ms;
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

  ${Container}:hover & {
    opacity: 1;
  }

  svg {
    fill: ${(props) => props.primaryColor};
  }
`;

const CurrentValue = styled.span<{
  textAlign?: string;
  isPlaceholder?: boolean;
  displayOnHover?: boolean;
  value?: string;
}>`
  flex: 1;
  text-align: ${({ textAlign }) => textAlign};
  margin-left: ${({ textAlign }) => (textAlign === 'center' ? 21 : 0)}px;
  color: ${({ isPlaceholder }) => (isPlaceholder ? '#b4b4b4' : 'inherit')};
  opacity: ${(props) => (props.displayOnHover && !props.value ? '0' : '1')};
  transition: opacity 100ms;

  ${Container}:hover & {
    opacity: 1;
  }
`;

interface FjEditableInputProps {
  value?: string;
  onUpdate?: (value: unknown) => void;
  validator?: (value: string) => string | null;
  displayEditOnHover?: boolean;
  primaryColor?: string;
  displayTransform?: (value: string) => string;
  returnTransform?: (value: string) => unknown;
  placeholder?: string;
  singleClick?: boolean;
  readOnly?: boolean;
  textAlign?: string;
  errorMessage?: string;
  className?: string;
  /**
   * Changes when the consumer rejects an edit, telling this field to drop its optimistic value
   * and show what is stored. Usually supplied by EditRevertContext rather than passed directly.
   */
  revertToken?: unknown;
  // Additional props are forwarded to the underlying FjInput (untyped .js)
  [key: string]: unknown;
}

interface FjEditableInputState {
  isEditing: boolean;
  value: string;
  inputValue: string;
  position: DOMRect | null;
  errorMessage?: string | null;
}

class FjEditableInput extends React.Component<FjEditableInputProps, FjEditableInputState> {
  static contextType = EditRevertContext;

  declare context: React.ContextType<typeof EditRevertContext>;

  static defaultProps = {
    value: '',
    onUpdate: () => {},
    validator: () => null,
    displayEditOnHover: false,
    primaryColor: '#156777',
    displayTransform: (a: string) => a,
    returnTransform: (a: string) => a,
    placeholder: '',
    singleClick: false,
    readOnly: false,
    textAlign: 'left',
  };

  state: FjEditableInputState = {
    isEditing: false,
    value: this.props.value as string,
    inputValue: this.props.value as string,
    position: null,
  };

  containerRef = React.createRef<HTMLDivElement>();

  /** The last revert signal acted on; see componentDidUpdate. */
  revertTokenSeen: unknown = undefined;

  /** An explicit prop wins over the context, so a caller outside a provider can still drive it. */
  revertToken = (): unknown =>
    this.props.revertToken !== undefined ? this.props.revertToken : this.context;

  /**
   * Discard the optimistic value and show what the props say is stored. This is what the cell
   * remount used to do by accident before FJCLOUD-4494 - now it happens because the consumer
   * asked for it.
   */
  revertToStoredValue = (): void => {
    const stored = this.props.value as string;
    // Nothing optimistic to drop: leave the field (and the other 12 on the row) alone.
    if (!this.state.isEditing && this.state.value === stored) return;
    this.setState({
      isEditing: false,
      value: stored,
      inputValue: stored,
      errorMessage: null,
    });
    lockr.rm(EDITABLE_INPUT_FIELD_ACTIVE);
    document.removeEventListener('mousedown', this.handleClick, false);
  };

  UNSAFE_componentWillUpdate(nextProps: FjEditableInputProps): void {
    this.subscribeToValue(nextProps);
    this.subscribeToError(nextProps);
  }

  componentDidMount(): void {
    this.revertTokenSeen = this.revertToken();
  }

  // The revert signal can arrive as a prop or through the context, so it is not in prevProps;
  // track what we last acted on instead of diffing arguments.
  componentDidUpdate(): void {
    const token = this.revertToken();
    if (token === this.revertTokenSeen) return;
    this.revertTokenSeen = token;
    this.revertToStoredValue();
  }

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  subscribeToValue = (nextProps: FjEditableInputProps): void => {
    if (this.props.value !== nextProps.value) {
      this.setState({
        isEditing: false,
        value: nextProps.value as string,
        inputValue: nextProps.value as string,
      });
      lockr.rm(EDITABLE_INPUT_FIELD_ACTIVE);
    }
  };

  subscribeToError = (nextProps: FjEditableInputProps): void => {
    if (this.props.errorMessage !== nextProps.errorMessage) {
      const position = this.containerRef?.current?.getBoundingClientRect() ?? null;
      this.setState({
        position,
      });
    }
  };

  onChange = (inputValue: string): void => {
    const { validator } = this.props;
    const errorMessage = validator!(inputValue);
    const position = this.containerRef?.current?.getBoundingClientRect() ?? null;
    this.setState({
      inputValue,
      errorMessage,
      position,
    });
  };

  onEdit = (): void => {
    if (this.props.readOnly) return;
    lockr.set(EDITABLE_INPUT_FIELD_ACTIVE, true);
    this.setState(({ value: inputValue }) => ({
      isEditing: true,
      inputValue,
    }));
    document.addEventListener('mousedown', this.handleClick, false);
  };

  onSubmit = (value: string): void => {
    const { returnTransform } = this.props;
    this.setState(
      ({ value: inputValue }) => ({
        isEditing: false,
        value,
        inputValue,
        errorMessage: null,
      }),
      () => this.props.onUpdate!(returnTransform!(this.state.value)),
    );
    lockr.rm(EDITABLE_INPUT_FIELD_ACTIVE);
    document.removeEventListener('mousedown', this.handleClick, false);
  };

  onCancel = (): void => {
    this.setState(({ value: inputValue }) => ({
      isEditing: false,
      inputValue,
      errorMessage: null,
    }));
    lockr.rm(EDITABLE_INPUT_FIELD_ACTIVE);
    document.removeEventListener('mousedown', this.handleClick, false);
  };

  handleClick = (e: MouseEvent): void => {
    if (this.containerRef.current!.contains(e.target as Node)) return;
    this.onCancel();
  };

  render(): React.ReactNode {
    const { value, inputValue, isEditing, errorMessage, position } = this.state;
    const {
      displayEditOnHover,
      validator,
      primaryColor,
      displayTransform,
      placeholder,
      className,
      readOnly,
      singleClick,
      textAlign,
      // Pulled out of `props` so it is not forwarded on to FjInput and into the DOM.
      revertToken: _revertToken,
      ...props
    } = this.props;
    const iconProps = {
      style: {
        // primaryColor is guaranteed defined via defaultProps
        primaryColor: primaryColor as string,
        bgColor: 'transparent',
      },
      size: '16px',
    };
    const lockIconProps = {
      ...iconProps,
      size: '12px',
    };
    const valueClickProp = this.props.singleClick
      ? { onClick: this.onEdit }
      : { onDoubleClick: this.onEdit };

    if (isEditing) {
      return (
        <Container editing ref={this.containerRef} className={className}>
          <Input
            autoFocus
            {...props}
            textAlign={textAlign}
            value={displayTransform!(inputValue)}
            onChange={this.onChange}
            onCancel={this.onCancel}
            onSubmit={this.onSubmit}
            placeholder={placeholder}
          />
          {/* These are the only way to commit or abandon an edit, and an icon on its own says
              nothing to a screen reader - or to a test, which was left picking them out by
              their position among the row's svgs. */}
          <IconContainer
            role="button"
            aria-label="Save"
            aria-disabled={!!errorMessage}
            $animation={acceptIconAnimation}
            onClick={() => this.onSubmit(inputValue)}
            disabled={!!errorMessage}
          >
            <AcceptIcon {...iconProps} />
          </IconContainer>
          <IconContainer
            role="button"
            aria-label="Cancel"
            $animation={cancelIconAnimation}
            onClick={this.onCancel}
          >
            <CancelIcon {...iconProps} />
          </IconContainer>
          {!!position && !!errorMessage && (
            // TODO FjFloatingError component
            <FjPortal>
              <FloatingError position={position}>{errorMessage}</FloatingError>
            </FjPortal>
          )}
        </Container>
      );
    }
    return (
      <Container className={className} readOnly={readOnly} value={value}>
        <CurrentValue
          isPlaceholder={!value}
          displayOnHover={displayEditOnHover}
          value={value}
          textAlign={textAlign}
          {...valueClickProp}
        >
          {displayTransform!(value || (placeholder as string))}
        </CurrentValue>
        <IconContainer
          // A lock is a statement, not a control; only the pencil is operable.
          {...(readOnly ? { 'aria-label': 'Locked' } : { role: 'button', 'aria-label': 'Edit' })}
          displayOnHover={displayEditOnHover}
          onClick={this.onEdit}
          readOnly={readOnly}
        >
          {readOnly ? <LockIcon {...lockIconProps} /> : <EditIcon {...iconProps} />}
        </IconContainer>
      </Container>
    );
  }
}

export default FjEditableInput;
